import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import retreatService from '../services/retreatService';

const RetreatCenter = () => {
  const navigate = useNavigate();
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countryId, setCountryId] = useState('USA');

  // Map country ID to description
  const countryMap = {
    '1': 'USA',
    '2': 'Canada',
    'USA': 'USA',
    'Canada': 'Canada'
  };

  useEffect(() => {
    fetchCenters();
  }, [countryId]);

  const fetchCenters = async () => {
    try {
      setLoading(true);
      const countryDesc = countryMap[countryId] || 'USA';
      const data = await retreatService.getCenters(countryDesc);
      setCenters(data || []);
      setError(null);
    } catch (err) {
      setError('Failed to load centers. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCenter = (centerId) => {
    navigate(`/retreat/selection?centerId=${centerId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container-custom">
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Select Retreat Center</h1>
          <p className="text-lg text-gray-600">
            Choose a center to view available retreats
          </p>
        </div>

        <div className="mb-8">
          <label className="form-label">Select Country</label>
          <select
            value={countryId}
            onChange={(e) => setCountryId(e.target.value)}
            className="form-input"
          >
            <option value="USA">USA</option>
            <option value="Canada">Canada</option>
          </select>
        </div>

        {loading && (
          <div className="text-center py-12">
            <div className="inline-block">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
            <p className="mt-4 text-gray-600">Loading centers...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {!loading && centers.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {centers.map((center) => (
              <div
                key={center.center_Id}
                className="item-card cursor-pointer"
                onClick={() => handleSelectCenter(center.center_Id)}
              >
                {/* Image Section */}
                {center.center_Logo_URL && (
                  <div className="h-48 bg-gradient-to-br from-purple-400 to-pink-400 overflow-hidden relative rounded-t-[18px]">
                    <img
                      src={center.center_Logo_URL?.trim()}
                      alt={center.center_Name}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/400x300?text=Retreat+Center';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                )}
                
                {/* Content Section */}
                <div className="p-6 relative z-10">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {center.center_Name}
                  </h3>
                  
                  <p className="text-gray-700 mb-4 line-clamp-3 text-sm leading-relaxed">
                    {center.center_Desc}
                  </p>
                  
                  {center.camp_Count && (
                    <div className="mb-6 inline-block bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold">
                      🧘 {center.camp_Count} retreat{center.camp_Count !== 1 ? 's' : ''} available
                    </div>
                  )}
                  
                  <button className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 px-4 rounded-xl transition transform hover:shadow-lg">
                    View Retreats →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && centers.length === 0 && !error && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No centers available for the selected country.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RetreatCenter;
